import axios from 'axios';
import type { Expense } from '../types';
import { getChauffeurById } from './userApi';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Define an interface for the raw expense data from API
interface RawExpenseData {
  id: string;
  driver_id?: string | null;
  driverId?: string | null;
  driver?: string | null;
  payment_method?: string;
  paymentMethod?: string;
  sync_status?: string;
  syncStatus?: string;
  type: string;
  entryType: string;
  date: string;
  quantity?: number | null;
  amount: number;
  description?: string | null;
  location?: string | null;
  imageUrl?: string | null;
  time?: string;
  periodMonth?: number | null;
  periodYear?: number | null;
  truckId?: string | null;
}

interface ListExpensesResponse {
  rows: Expense[];
  count: number;
  totals: { totalAmount: number; byType: Record<string, number> };
}

interface GetAllExpensesResponse {
  expenses?: RawExpenseData[];
  total?: number;
}

export const getExpense = async (id: string): Promise<Expense> => {
  try {
    const response = await api.get<Expense>(`/api/expenses/${id}`);
    return response.data;
  } catch (error: any) {
    if (error?.response) {
      const { error: errorMessage } = error.response.data as { error?: string };
      throw new Error(
        errorMessage || 'Erreur lors de la récupération de la dépense.'
      );
    }
    throw new Error('Erreur inconnue lors de la récupération de la dépense.');
  }
};

export const listExpenses = async (params: Record<string, any> = {}): Promise<ListExpensesResponse> => {
  const { data } = await api.get<ListExpensesResponse>('/api/expenses', { params });
  return { rows: data.rows || [], count: data.count || 0, totals: data.totals || { totalAmount: 0, byType: {} } };
};

export const exportExpenses = async (params: Record<string, any> = {}, format: 'csv'|'xlsx'|'pdf'='csv'): Promise<void> => {
  const res = await api.get('/api/expenses/export', { 
    params: { ...params, format }, 
    responseType: 'blob' 
  });
  
  const blob = new Blob([res.data as BlobPart], { 
    type: res.headers['content-type'] || 'application/octet-stream' 
  });
  
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const filename = (res.headers['content-disposition'] || '').split('filename=')[1]?.replace(/"/g,'') || `expenses.${format}`;
  a.href = url; 
  a.download = filename; 
  a.click(); 
  URL.revokeObjectURL(url);
};

export const getAllExpenses = async (
  page: number = 1,
  pageSize: number = 10,
  date?: string
): Promise<{ expenses: Expense[]; total: number }> => {
  try {
    const response = await api.get<RawExpenseData[] | GetAllExpensesResponse>('/api/firebase/expenses', {
      params: { page, pageSize, date },
    });    
    let expenses: Expense[] = [];
    let total = 0;
    
    if (Array.isArray(response.data)) {
      expenses = await Promise.all(
        response.data.map(async (expense: RawExpenseData) => {
          let driverName = 'Non assigné';
          const driverId =
            expense.driver_id || expense.driver || expense.driverId || null;
          if (driverId) {
            try {
              const chauffeur = await getChauffeurById(driverId);
              driverName = chauffeur.nom || 'Nom non disponible';
              console.log(
                `getChauffeurById success for driverId ${driverId}:`,
                { nom: chauffeur.nom }
              );
            } catch (err) {
              console.error(
                `Erreur lors de la récupération du chauffeur ${driverId}:`,
                err
              );
            }
          }
          return {
            ...expense,
            driverId,
            driverName,
            paymentMethod: expense.payment_method || expense.paymentMethod || 'N/A',
            syncStatus: expense.sync_status || expense.syncStatus || 'N/A',
          } as Expense;
        })
      );
      total = response.data.length;
    } else if (response.data && response.data.expenses && Array.isArray(response.data.expenses)) {
      expenses = await Promise.all(
        response.data.expenses.map(async (expense: RawExpenseData) => {
          let driverName = 'Non assigné';
          const driverId =
            expense.driver_id || expense.driver || expense.driverId || null;
          if (driverId) {
            try {
              const chauffeur = await getChauffeurById(driverId);
              driverName = chauffeur.nom || 'Nom non disponible';
              console.log(
                `getChauffeurById success for driverId ${driverId}:`,
                { nom: chauffeur.nom }
              );
            } catch (err) {
              console.error(
                `Erreur lors de la récupération du chauffeur ${driverId}:`,
                err
              );
            }
          }
          return {
            ...expense,
            driverId,
            driverName,
            paymentMethod: expense.payment_method || expense.paymentMethod || 'N/A',
            syncStatus: expense.sync_status || expense.syncStatus || 'N/A',
          } as Expense;
        })
      );
      total = Number(response.data.total) || expenses.length;
    }
    
    console.log('getAllExpenses processed response:', {
      expenses: expenses.map((e) => ({
        id: e.id,
        driverId: e.driverId,
        driverName: e.driverName,
        paymentMethod: e.paymentMethod,
        syncStatus: e.syncStatus,
      })),
      total,
    });
    
    return { expenses, total };
  } catch (error: any) {
    if (error?.response) {
      const { error: errorMessage } = error.response.data as { error?: string };
      throw new Error(
        errorMessage || 'Erreur lors de la récupération des dépenses.'
      );
    }
    throw new Error('Erreur inconnue lors de la récupération des dépenses.');
  }
};

export const createExpense = async (expense: Partial<Expense>): Promise<Expense> => {
  try {
    const response = await api.post<Expense>('/api/expenses', expense);
    return response.data;
  } catch (error: any) {
    if (error?.response) {
      const { error: errorMessage, details } = error.response.data as { 
        error?: string; 
        details?: string[] 
      };
      throw new Error(
        Array.isArray(details)
          ? details.join(', ')
          : errorMessage || 'Erreur lors de la création de la dépense.'
      );
    }
    throw new Error('Erreur inconnue lors de la création de la dépense.');
  }
};

export const updateExpense = async (
  id: string,
  expense: Partial<Expense>
): Promise<Expense> => {
  try {
    const response = await api.patch<Expense>(`/api/expenses/${id}`, expense);
    return response.data;
  } catch (error: any) {
    if (error?.response) {
      const { error: errorMessage, details } = error.response.data as {
        error?: string;
        details?: string[];
      };
      throw new Error(
        Array.isArray(details)
          ? details.join(', ')
          : errorMessage || 'Erreur lors de la mise à jour de la dépense.'
      );
    }
    throw new Error('Erreur inconnue lors de la mise à jour de la dépense.');
  }
};

export const deleteExpense = async (id: string): Promise<void> => {
  try {
    await api.delete(`/api/expenses/${id}`);
  } catch (error: any) {
    if (error?.response) {
      const { error: errorMessage } = error.response.data as { error?: string };
      throw new Error(
        errorMessage || 'Erreur lors de la suppression de la dépense.'
      );
    }
    throw new Error('Erreur inconnue lors de la suppression de la dépense.');
  }
};